/**
 * AutoMapa service partnerships (Milestone 38) — client constants.
 */

export const PARTNER_CATEGORIES = [
  "financing",
  "insurance",
  "soat",
  "inspection",
  "transfer_service",
  "warranty_placeholder",
  "roadside_assistance_placeholder",
  "other",
];

export const PARTNER_CATEGORY_LABELS_ES = {
  financing: "Financiación",
  insurance: "Seguro",
  soat: "SOAT",
  inspection: "Peritaje",
  transfer_service: "Traspaso",
  warranty_placeholder: "Garantía",
  roadside_assistance_placeholder: "Asistencia",
  other: "Otro servicio",
};

export const PARTNER_STATUSES = ["draft", "active", "paused", "suspended", "archived"];

export const PARTNER_STATUS_LABELS = {
  draft: "Borrador",
  active: "Activo",
  paused: "Pausado",
  suspended: "Suspendido",
  archived: "Archivado",
};

export const LEAD_DELIVERY_METHODS = [
  "admin_manual",
  "email_placeholder",
  "whatsapp_placeholder",
  "webhook_placeholder",
];

export const COMMISSION_MODELS = [
  "none",
  "referral_fee_placeholder",
  "revenue_share_placeholder",
  "manual_placeholder",
  "unknown",
];

export const LEAD_DELIVERY_LABELS = {
  admin_manual: "Manual (admin)",
  email_placeholder: "Email (placeholder)",
  whatsapp_placeholder: "WhatsApp (placeholder)",
  webhook_placeholder: "Webhook (placeholder)",
};

export const PARTNER_LEAD_STATUS_LABELS = {
  new: "Nuevo",
  routed: "Enrutado",
  delivered: "Entregado",
  contacted: "Contactado",
  qualified: "Calificado",
  converted: "Convertido",
  lost: "Perdido",
  duplicate: "Duplicado",
  invalid: "Inválido",
  archived: "Archivado",
};

export const SERVICE_PAGE_SLUGS = {
  financing: "financiacion",
  insurance: "seguros",
  soat: "soat",
  inspection: "peritaje",
  transfer_service: "traspaso",
};

export const SLUG_TO_CATEGORY = Object.fromEntries(
  Object.entries(SERVICE_PAGE_SLUGS).map(([cat, slug]) => [slug, cat])
);

export const SERVICE_PAGE_CONFIG = {
  financing: {
    slug: "financiacion",
    title: "Financiación para comprar carro usado",
    hero: "Consulta opciones de financiación con aliados externos",
    whenToUse: "Cuando necesitas crédito o comparar cuota inicial, plazo y condiciones antes de comprar.",
    faq: [
      { q: "¿AutoMapa aprueba créditos?", a: "No. AutoMapa puede conectar tu solicitud con un aliado. La aprobación depende del proveedor." },
      { q: "¿Qué información necesito?", a: "Datos básicos de contacto y, si aplica, el vehículo que te interesa." },
    ],
    checklistItems: ["financing_costs"],
    guideLinks: [{ label: "Guía compra segura", href: "/guias/compra-segura-carro-usado" }],
  },
  insurance: {
    slug: "seguros",
    title: "Seguro vehicular",
    hero: "Solicita información sobre seguros con aliados",
    whenToUse: "Antes o después de comprar, para entender coberturas y costos.",
    faq: [
      { q: "¿AutoMapa vende seguros?", a: "No. Conectamos tu solicitud con aliados. Precio y cobertura dependen del proveedor." },
    ],
    checklistItems: ["after_update_insurance"],
    guideLinks: [{ label: "Checklist de compra", href: "/guias/checklist-compra-carro-usado" }],
  },
  soat: {
    slug: "soat",
    title: "SOAT y revisión técnico-mecánica",
    hero: "Consulta SOAT y requisitos con aliados",
    whenToUse: "Para verificar vigencia o planear emisión antes de transferir o circular.",
    faq: [
      { q: "¿AutoMapa emite SOAT?", a: "No. La emisión depende del proveedor correspondiente." },
    ],
    checklistItems: ["soat_valid", "tecnomecanica_valid"],
    guideLinks: [{ label: "Guía SOAT", href: "/guias/soat-tecnomecanica-carro-usado" }],
  },
  inspection: {
    slug: "peritaje",
    title: "Peritaje e inspección pre-compra",
    hero: "Agenda información sobre peritaje con un aliado externo",
    whenToUse: "Antes de pagar o transferir, especialmente si hay señales de riesgo o poca información.",
    faq: [
      { q: "¿AutoMapa inspecciona el vehículo?", a: "No. El peritaje lo realiza un tercero. AutoMapa no garantiza el resultado." },
    ],
    checklistItems: ["mechanical_inspection", "independent_inspection_offer"],
    guideLinks: [{ label: "Compra segura", href: "/guias/compra-segura-carro-usado" }],
  },
  transfer_service: {
    slug: "traspaso",
    title: "Ayuda con traspaso vehicular",
    hero: "Solicita orientación sobre trámite de traspaso",
    whenToUse: "Cuando necesitas apoyo con documentos, pagos y pasos ante tránsito.",
    faq: [
      { q: "¿AutoMapa hace el traspaso?", a: "No. El trámite depende de documentos, autoridades y validaciones correspondientes." },
    ],
    checklistItems: ["transfer_process"],
    guideLinks: [{ label: "Checklist traspaso", href: "/guias/checklist-compra-carro-usado" }],
  },
};

export const CHECKLIST_ITEM_PARTNER_CATEGORY = {
  financing_costs: "financing",
  soat_valid: "soat",
  tecnomecanica_valid: "soat",
  after_update_insurance: "insurance",
  mechanical_inspection: "inspection",
  independent_inspection_offer: "inspection",
  transfer_process: "transfer_service",
};

export const CATEGORY_DISCLOSURES_ES = {
  financing:
    "La aprobación, tasa, plazo, cuota inicial y condiciones dependen de la entidad o aliado financiero.",
  insurance: "La cobertura, precio y condiciones dependen de la aseguradora o aliado.",
  soat: "La disponibilidad y emisión del SOAT dependen del proveedor correspondiente.",
  inspection:
    "El peritaje o inspección es realizado por un tercero. AutoMapa no garantiza el resultado mecánico, legal o documental del vehículo.",
  transfer_service:
    "El trámite de traspaso depende de documentos, autoridades, pagos y validaciones correspondientes.",
  warranty_placeholder: "La garantía y condiciones dependen del proveedor correspondiente.",
  roadside_assistance_placeholder: "La asistencia y cobertura dependen del proveedor correspondiente.",
  other: "Las condiciones del servicio dependen del aliado correspondiente.",
};

export const GENERAL_PARTNERSHIP_DISCLOSURE_ES =
  "AutoMapa puede conectar tu solicitud con un aliado externo. El servicio, condiciones, aprobación, precio y respuesta dependen del aliado. AutoMapa no garantiza aprobación, cobertura, inspección, financiación, seguro, SOAT, traspaso ni resultado final.";

export const PARTNER_SUCCESS_MESSAGE =
  "Recibimos tu solicitud. AutoMapa o un aliado podrá contactarte con más información. Las condiciones dependen del proveedor del servicio.";

export const PARTNERSHIP_ANALYTICS_EVENTS = {
  SERVICE_CARD_VIEWED: "partner_service_card_viewed",
  SERVICE_CTA_CLICKED: "partner_service_cta_clicked",
  FORM_OPENED: "partner_lead_form_opened",
  FORM_SUBMITTED: "partner_lead_form_submitted",
  DISCLOSURE_VIEWED: "partner_disclosure_viewed",
  DISCLOSURE_ACCEPTED: "partner_disclosure_accepted",
};

export function getCategoryLabel(category) {
  return PARTNER_CATEGORY_LABELS_ES[category] || category;
}

export function getServicePath(category) {
  const slug = SERVICE_PAGE_SLUGS[category];
  return slug ? `/servicios/${slug}` : "/servicios";
}

export function statusBadgeClass(status) {
  if (status === "active") return "badge badge--success";
  if (status === "paused" || status === "suspended") return "badge badge--warn";
  if (status === "archived") return "badge badge--muted";
  return "badge";
}

export function partnerLeadStatusBadgeClass(status) {
  if (status === "qualified" || status === "converted") return "badge badge--success";
  if (status === "lost" || status === "invalid") return "badge badge--danger";
  if (status === "routed" || status === "delivered") return "badge badge--info";
  return "badge";
}
